import { useEffect } from "react";
import { isPWAInstalled, isMobileDevice } from "../pwaHelpers";

/**
 * Locks screen orientation to portrait in PWA mode on mobile devices.
 * Automatically unlocks when a video/iframe enters fullscreen so users
 * can watch YouTube/Vimeo in landscape, then re-locks on exit.
 */
const useScreenOrientation = () => {
  useEffect(() => {
    if (!isMobileDevice() || !isPWAInstalled()) return;

    const lockOrientation = () => {
      try {
        screen.orientation?.lock("portrait-primary").catch(() => {});
      } catch {
        // API not supported
      }
    };

    const unlockOrientation = () => {
      try {
        screen.orientation?.unlock();
      } catch {
        // API not supported
      }
    };

    const isVideoFullscreen = () => {
      const el = document.fullscreenElement;
      if (!el) return false;
      const tag = el.tagName?.toLowerCase();
      return (
        tag === "video" ||
        tag === "iframe" ||
        !!el.querySelector("video, iframe")
      );
    };

    const handleFullscreenChange = () => {
      if (document.fullscreenElement && isVideoFullscreen()) {
        unlockOrientation();
      } else if (!document.fullscreenElement) {
        lockOrientation();
      }
    };

    // Initial lock
    lockOrientation();

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
    };
  }, []);
};

export default useScreenOrientation;
